import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { throttle } from './throttle';

/**
 * Observe and detect if the content overflows its container.
 *
 * Notes:
 * - it remembers the last breakpoint at which the content was overflowing - this is
 *   needed for the app bar to detect when to switch from the compact to the compact app
 *   bar
 * - it uses ResizeObserver directly instead of e.g. react-resize-observer to avoid
 *   unnecessary rerenders
 * - it uses MutationObserver to detect changes in the content element, for example items
 *   being added or removed from the navigation bar
 */
export function useOverflowObserver(): {
  /**
   * A boolean indicating if the content is overflowing the container
   */
  isContentOverflowing: boolean;
  /**
   * A ref to be attached to the container element
   */
  containerNodeRef: MutableRefObject<HTMLDivElement | null>;
  /**
   * A ref to be attached to the content element
   */
  contentNodeRef: MutableRefObject<HTMLDivElement | null>;
} {
  const [breakpoint, setBreakpoint] = useState<number>();
  const [isOverflowing, setOverflowing] = useState(false);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const contentNodeRef = useRef<HTMLDivElement | null>(null);

  // Effect event to prevent throttle method from being reinitialized when React state
  // changes (https://mattrossman.com/2024/09/30/non-reactive-callbacks-in-react)
  const checkOverflow = useCallback(
    (containerWidth: number) => {
      // Check if content fits into the container (i.e. if it's scrollable)
      if (contentNodeRef.current) {
        // hasScrollContainer() is critical - if it can't deterministically check if the
        // node contains a scroll container, the 'isContentOverflowing' flag may start to
        // flicker between true/false
        const isOverflowingNext = hasScrollContainer(contentNodeRef.current);
        const breakpointNext = isOverflowingNext ? containerWidth : breakpoint;
        if (isOverflowing !== isOverflowingNext) {
          setOverflowing(isOverflowingNext);
        }
        // Remember the breakpoint in case content node disappears from DOM
        if (breakpointNext !== breakpoint) {
          setBreakpoint(breakpointNext ? Math.floor(breakpointNext) : undefined);
        }
      }
      // If content node does not exist, we need to fall back to checking if we reached the
      // last remembered breakpoint
      if (!contentNodeRef.current && breakpoint) {
        const isBelowBreakpoint = containerWidth <= breakpoint;
        // Delay updates by setting a minimum distance from the breakpoint. This is not
        // needed above when content element is in DOM. Without this delay, updates just
        // near the breakpoint may flicker.
        const shouldUpdate = Math.abs(containerWidth - breakpoint) > 30;
        if (shouldUpdate && isBelowBreakpoint !== isOverflowing) {
          setOverflowing(isBelowBreakpoint);
        }
      }
    },
    [breakpoint, isOverflowing],
  );

  const checkOverflowRef = useRef(checkOverflow);

  useInsertionEffect(() => {
    checkOverflowRef.current = checkOverflow;
  }, [checkOverflow]);

  // Measure before the first paint
  useLayoutEffect(() => {
    if (!containerNodeRef.current || !contentNodeRef.current) {
      return;
    }
    const containerWidth = containerNodeRef.current.getBoundingClientRect().width;
    const isOverflowingNext = hasScrollContainer(contentNodeRef.current);
    if (isOverflowingNext) {
      setOverflowing(isOverflowingNext);
      setBreakpoint(Math.floor(containerWidth));
    }
  }, []);

  useEffect(() => {
    if (!containerNodeRef.current) {
      return;
    }
    const checkOverflowThrottled = throttle((entries: ResizeObserverEntry[] | MutationRecord[]) => {
      // Only one element is observed, we can ignore previous entries
      const entry = entries.at(-1);

      if (entry instanceof ResizeObserverEntry) {
        const containerWidth = entry.contentRect.width;
        checkOverflowRef.current(containerWidth);
      }
      if (entry instanceof MutationRecord) {
        // Triggers reflow (unlike resize observer)
        const containerWidth = containerNodeRef.current?.getBoundingClientRect().width;
        if (containerWidth) {
          checkOverflowRef.current(containerWidth);
        }
      }
      // Corresponds to 20 frames at 60 Hz.
    }, 166 * 2);

    const resizeObserver = new ResizeObserver(checkOverflowThrottled);
    resizeObserver.observe(containerNodeRef.current);
    const observer = new MutationObserver(checkOverflowThrottled);
    // Observe container instead of content to detect more relevant changes to layout
    observer.observe(containerNodeRef.current, { childList: true, subtree: true });
    return () => {
      checkOverflowThrottled.clear();
      resizeObserver.disconnect();
      observer.disconnect();
    };
  }, [checkOverflowRef]);

  return { isContentOverflowing: isOverflowing, containerNodeRef, contentNodeRef };
}

/**
 * Find the first scrollable descendant of a node. If it exist, it means the some content
 * is clipped by the container.
 */
function hasScrollContainer(node: HTMLElement): boolean {
  const isScrollable = node.offsetWidth < node.scrollWidth;
  if (
    !isScrollable &&
    // Only step into the child node if it doesn't have siblings (otherwise we cannot
    // determine which sibling is the scroll container)
    node.firstElementChild === node.lastElementChild &&
    node.firstElementChild instanceof HTMLElement
  ) {
    return hasScrollContainer(node.firstElementChild);
  }
  return isScrollable;
}
